import { useCallback, useEffect, useMemo, useState } from 'react';
import authService, { type PhoneVerificationStatus } from '../../services/auth.service';
import PhoneVerificationSection from '../security/PhoneVerificationSection';
import contractsService from '../../services/contracts.service';
import schedulingService, {
  type BookableSlot,
  type MyAgentSlotsResponse,
} from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import type { AssistanceType } from '../../types/contracts';
import { useMovablePopup } from '../MovablePopup';
import { useModalFocus } from '../../hooks/useModalFocus';
import { readerLocale } from '../../i18n/language';

interface Props {
  caseId: string;
  /** Pre-selects the channel when the modal opens. */
  initialType: AssistanceType;
  onClose: () => void;
  onSubmitted: () => void;
}

const NOTES_MAX = 500;

/**
 * Get-help modal with two channels:
 *   1. await_contact — agent will call back on the number the account holds,
 *      which was proven at onboarding. A client with no proven number is
 *      OFFERED the one-time-code flow here, never forced through it: this is
 *      someone already stuck asking for help, and the advisor can still reach
 *      them by email. The request lands in case_data.client_assistance_request
 *      and an outbox event notifies the assigned agent.
 *   2. schedule_call — real slot picker against the assigned agent's
 *      availability + time-off + existing bookings (computed server-
 *      side). Confirms the slot atomically via POST /scheduling/
 *      appointments — the partial unique index protects against double-
 *      booking races.
 *
 * Future (client-schedule-page): once we ship a general /client/schedule
 * page (booking outside the contract review flow), it can reuse the
 * slot-picking section below — pull it out into a shared component
 * then. For MVP the modal is the only entry point and the picker lives
 * inline.
 */
export default function AssistanceModal({
  caseId,
  initialType,
  onClose,
  onSubmitted,
}: Readonly<Props>): JSX.Element {
  const [type, setType] = useState<AssistanceType>(initialType);
  const [profileError, setProfileError] = useState<string | null>(null);

  // await_contact extras
  // Whether the account holds a proven number. Decides what to SHOW only —
  // nothing here blocks the request.
  const [phoneStatus, setPhoneStatus] = useState<PhoneVerificationStatus | null>(null);

  // schedule_call extras
  const [slotsLoading, setSlotsLoading] = useState<boolean>(false);
  const [slotsResponse, setSlotsResponse] = useState<MyAgentSlotsResponse | null>(null);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlotIso, setSelectedSlotIso] = useState<string | null>(null);

  const popup = useMovablePopup<HTMLDialogElement>();
  // Shared
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset to the parent's preset and clear errors when the modal re-opens.
  useEffect(() => {
    setType(initialType);
    setSubmitError(null);
  }, [initialType]);

  // Modal for the keyboard: focus moves in, stays in and returns to the opener. ESC
  // closes (only when not mid-submit so we don't drop a half-done network call without
  // feedback).
  useModalFocus(popup.panelRef, true, submitting ? undefined : onClose);

  // Is there a PROVEN number to call? Asked of the verification endpoint, not
  // read off the profile: a number on file is not the same fact as a number we
  // proved.
  const loadPhoneStatus = useCallback(async (): Promise<void> => {
    try {
      setPhoneStatus(await authService.getPhoneVerification());
      setProfileError(null);
    } catch (err: unknown) {
      setProfileError(getApiErrorMessage(err, 'Could not check your phone number.'));
    }
  }, []);

  useEffect(() => { void loadPhoneStatus(); }, [loadPhoneStatus]);

  // Lazy-load slots only when the user picks schedule_call and we haven't
  // already fetched. Re-fetching on every type-toggle would be wasteful;
  // re-fetching after a submit error is cheap enough that we trigger a
  // refresh on a stale-slot 409 by clearing slotsResponse below.
  useEffect(() => {
    if (type !== 'schedule_call' || slotsResponse !== null || slotsLoading) return;
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);
    schedulingService
      .listMyAgentSlots()
      .then((res) => { if (!cancelled) setSlotsResponse(res); })
      .catch((err: unknown) => {
        if (!cancelled) setSlotsError(getApiErrorMessage(err, 'Could not load available times.'));
      })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [type, slotsResponse, slotsLoading]);

  // Group slots by local-date so the UI can render one row per day.
  const slotsByDay = useMemo<{ key: string; label: string; slots: BookableSlot[] }[]>(() => {
    if (!slotsResponse) return [];
    const groups = new Map<string, BookableSlot[]>();
    for (const slot of slotsResponse.slots) {
      const localDate = new Date(slot.starts_at);
      const key = localDate.toDateString();
      const existing = groups.get(key);
      if (existing) existing.push(slot);
      else groups.set(key, [slot]);
    }
    return Array.from(groups.entries()).map(([key, slots]) => {
      const label = new Date(slots[0].starts_at).toLocaleDateString(readerLocale(), {
        weekday: 'long', month: 'short', day: 'numeric',
      });
      return { key, label, slots };
    });
  }, [slotsResponse]);

  const phoneVerified = phoneStatus?.verified === true;
  const offerPhone = type === 'await_contact' && phoneStatus !== null && !phoneVerified;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (type === 'await_contact') {
        await contractsService.requestAssistance(caseId, {
          type: 'await_contact',
          notes: notes.trim() || null,
        });
        onSubmitted();
      } else {
        // schedule_call
        if (!selectedSlotIso) {
          setSubmitError('Please pick a time.');
          setSubmitting(false);
          return;
        }
        try {
          await schedulingService.bookAppointment({
            starts_at: selectedSlotIso,
            purpose: notes.trim() || null,
            case_id: caseId,
          });
          onSubmitted();
        } catch (err: unknown) {
          // 409 = slot just taken / member already has appointment.
          // Refetch slots so the user sees the new picture.
          setSubmitError(getApiErrorMessage(err, 'Could not book the slot.'));
          setSelectedSlotIso(null);
          setSlotsResponse(null);  // triggers refetch via the effect
          setSubmitting(false);
          return;
        }
      }
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Could not submit your request.'));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  }

  const inProgressLabel = type === 'schedule_call' ? 'Booking…' : 'Sending…';
  const idleLabel = type === 'schedule_call' ? 'Book selected slot' : 'Send request';
  const submitLabel = submitting ? inProgressLabel : idleLabel;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(6,9,26,0.55)' }}
    >
      <dialog
        ref={popup.panelRef}
        open
        aria-modal="true"
        aria-labelledby="assistance-modal-title"
        tabIndex={-1}
        className="w-full rounded-2xl p-6 overflow-y-auto"
        style={{
          ...popup.panelStyle,
          outline: 'none',
          background: colors.surface,
          maxHeight: '90vh',
          margin: 0,
          border: 'none',
          maxWidth: '42rem',
          color: 'inherit',
        }}
        onCancel={(e) => { if (submitting) e.preventDefault(); else onClose(); }}
      >
        {popup.bar}
        <h2 id="assistance-modal-title" className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
          Get help with your contract
        </h2>
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          Tell us how you&apos;d like to be contacted and we&apos;ll have your Account Executive reach out.
        </p>

        {profileError && (
          <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
            {profileError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium mb-1" style={{ color: colors.ink }}>
              Preferred channel
            </legend>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg"
                   style={{ border: `1px solid ${type === 'await_contact' ? colors.gold : colors.border}` }}>
              <input
                type="radio"
                name="assistance-type"
                value="await_contact"
                checked={type === 'await_contact'}
                onChange={() => { setType('await_contact'); setSubmitError(null); }}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: colors.inkSoft }}>
                <span className="font-medium block" style={{ color: colors.ink }}>Await contact</span>
                {/* */}
                Your Account Executive will reach out via phone within 1 business day.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg"
                   style={{ border: `1px solid ${type === 'schedule_call' ? colors.gold : colors.border}` }}>
              <input
                type="radio"
                name="assistance-type"
                value="schedule_call"
                checked={type === 'schedule_call'}
                onChange={() => { setType('schedule_call'); setSubmitError(null); }}
                className="mt-1"
              />
              <span className="text-sm" style={{ color: colors.inkSoft }}>
                <span className="font-medium block" style={{ color: colors.ink }}>Schedule a call</span>
                {/* */}
                Pick a real time slot from your Account Executive&apos;s calendar. Booked instantly.
              </span>
            </label>
          </fieldset>

          {/* await_contact body */}
          {type === 'await_contact' && (
            <div className="space-y-3">
              {phoneVerified && (
                <div className="text-sm p-3 rounded-lg"
                     style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.borderSoft}` }}>
                  We&apos;ll call you at <span className="font-mono">{phoneStatus?.phone_masked}</span>.
                </div>
              )}
              {offerPhone && (
                <div className="p-3 rounded-lg"
                     style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}>
                  <p className="text-sm mb-1" style={{ color: colors.inkSoft }}>
                    We don&apos;t have a confirmed number for you. Add one so your Account Executive can call — or send the request as it is and we&apos;ll reach you by email.
                  </p>
                  <PhoneVerificationSection embedded onVerified={() => { void loadPhoneStatus(); }} />
                </div>
              )}
            </div>
          )}

          {/* schedule_call body */}
          {type === 'schedule_call' && (
            <div className="space-y-3">
              {slotsResponse && (
                <div className="text-xs" style={{ color: colors.mutedSoft }}>
                  Available times with{' '}
                  <span className="font-medium" style={{ color: colors.ink }}>
                    {fullName(slotsResponse.agent)}
                  </span>{' '}
                  · {slotsResponse.slot_duration_minutes}-minute slots · times shown in your local timezone
                </div>
              )}

              {slotsLoading && (
                <p className="text-sm" style={{ color: colors.muted }}>
                  Loading available times…
                </p>
              )}

              {slotsError && (
                <div className="rounded-lg p-3 text-sm" role="alert"
                     style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
                  {slotsError}
                </div>
              )}

              {!slotsLoading && !slotsError && slotsByDay.length === 0 && slotsResponse && (
                <div className="text-sm p-3 rounded-lg"
                     style={{ background: colors.canvas, color: colors.muted, border: `1px solid ${colors.borderSoft}` }}>
                  No slots available in the next 30 days. Try &quot;Await contact&quot; — your Account Executive will reach out.
                </div>
              )}

              {slotsByDay.length > 0 && (
                <div className="space-y-3">
                  {slotsByDay.map((group) => (
                    <div key={group.key}>
                      <div className="text-xs font-bold uppercase mb-2" style={{ color: colors.muted }}>
                        {group.label}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {group.slots.map((slot) => {
                          const isSelected = selectedSlotIso === slot.starts_at;
                          const timeLabel = new Date(slot.starts_at).toLocaleTimeString(readerLocale(), {
                            hour: '2-digit', minute: '2-digit',
                          });
                          return (
                            <button
                              key={slot.starts_at}
                              type="button"
                              aria-pressed={isSelected}
                              onClick={() => setSelectedSlotIso(slot.starts_at)}
                              className="px-3 py-1.5 rounded-lg text-sm transition-colors"
                              style={{
                                background: isSelected ? colors.navy : colors.surface,
                                color: isSelected ? '#FFFFFF' : colors.ink,
                                border: `1px solid ${isSelected ? colors.navy : colors.border}`,
                              }}
                            >
                              {timeLabel}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="block text-sm">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
              {type === 'schedule_call' ? 'Purpose' : 'Notes'}{' '}
              <span className="font-normal" style={{ color: colors.mutedSoft }}>
                (optional, max {NOTES_MAX} chars)
              </span>
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={NOTES_MAX}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.controlBorder}`, color: colors.ink }}
              placeholder={type === 'schedule_call'
                ? 'What would you like to discuss?'
                : 'Anything else we should know?'}
            />
          </label>

          {submitError && (
            <div className="rounded-lg p-3 text-sm" role="alert"
                 style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: colors.surface,
                color: colors.inkSoft,
                border: `1px solid ${colors.border}`,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (type === 'schedule_call' && !selectedSlotIso)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: colors.navy,
                color: '#FFFFFF',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
